<script setup lang="ts">
import Enums from "@/Enums";
import ChqSelect from "@ui/select/ChqSelect.vue";
import helpers, { changeClinicContext } from "@utils/helpers";
import Permissions from "@utils/Permissions";
import axios from "axios";
import { onMounted, ref, watch } from 'vue';
import { useRoute } from "vue-router";
import ChqMenuItem from '../components/menu/ChqMenuItem.vue';
import sidebarService, { uiSettings } from '../components/sidebar-service/sidebar.service';
import {
    appVm, isTrainingClinic, module, openQuickSearch,
    setupLookup, sidebarContainer, systemUser, systemUserLoaded,
    toggleMenu, toggleSettingsMenu
} from '../main-store';
import AccountingMenu from './accounting/AccountingMenu.vue';
import AdminMenu from "./admin/AdminMenu.vue";
import ClientsMenu from './clients/ClientsMenu.vue';
import ReportsMenu from './reports/ReportsMenu.vue';
import SettingsMenu from './settings/SettingsMenu.vue';

const route = useRoute();

onMounted(() => sidebarService.init());
watch(() => route.path, handleSidebarState, { immediate: true, once: true });

const menuItems = ref(1);
const clinicId = ref<number | null>(null);
const clinics = ref<any[]>([]);

systemUserLoaded.promise.then(function () {
    menuItems.value += boolToInt(systemUser.clinic.enableSpayNeuterTimeScheduling);
    menuItems.value += boolToInt(systemUser.clinic.enableWellnessScheduling);

    loadClinics();
})

function boolToInt(value: boolean | undefined): number {
    return value ? 1 : 0;
}

function handleSidebarState() {
    if (module.value === 'settings') {
        appVm.sidebarState.settings.menuVisible = true;
    }

    const activeMenu = sidebarContainer?.value?.querySelector('li.active') as HTMLElement

    if (sidebarContainer.value == null || activeMenu == null) {
        return
    }

    sidebarContainer.value.scrollTo({
        top: activeMenu.offsetTop - sidebarContainer.value.offsetTop + sidebarContainer.value.scrollTop,
        left: 0,
        behavior: 'smooth'
    });
}

function loadClinics() {
    if (!systemUser?.isSuperAdmin || isTrainingClinic.value) {
        return;
    }

    axios.get('/api/account/adminClinics')
        .then(function (response) {
            clinics.value = response.data.clinics;
            clinicId.value = response.data.clinicId;
        });
}

function onClinicChange() {
    if (clinicId.value == null) {
        return;
    }

    helpers.changeClinicContext(clinicId.value);
}

</script>
<template>
    <div class="group/navbar absolute inset-0 z-[55] hover:z-[57] duration-300 transition-[width] hidden-print"
        :class="{ 'w-0 lg:w-4': !appVm.toggleSideMenu, 'w-72 2xl:w-96': appVm.toggleSideMenu }"
        v-if="appVm.layoutMode === 'sidebar'">

        <div class="absolute inset-0 bg-gray-900/80 lg:hidden"
            :class="{ 'w-screen': appVm.toggleSideMenu, 'w-0': !appVm.toggleSideMenu }"></div>

        <div class="fixed left-80 top-0 flex w-16 justify-center lg:hidden" v-if="appVm.toggleSideMenu">
            <button type="button" class="p-4" @click="toggleMenu">
                <span class="sr-only">Close sidebar</span>
                <svg class="size-6 text-white" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
                    data-slot="icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
            </button>
        </div>

        <!-- check if isTrainingClinic works here -->
        <div v-if="!route.path.startsWith('/admin')"
            class="fixed h-full top-0 w-72 2xl:w-96 lg:group-hover/navbar:translate-x-0 grid grid-rows-[max-content_1fr_max-content] transition-transform duration-300"
            :class="{ '-translate-x-[calc(100%+16px)]': !appVm.toggleSideMenu, 'translate-x-0': appVm.toggleSideMenu, 'bg-amber-400 training': isTrainingClinic, 'bg-brand-900': !isTrainingClinic }">
            <div class="flex flex-col">
                <div class="p-2 relative">
                    <button type="button"
                        class="group w-full rounded-md bg-gray-100 px-4 py-3 text-left dropdown-toggle font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
                        data-toggle="dropdown" v-dropdown-toggle>
                        <span class="flex w-full items-center justify-between">
                            <span class="flex min-w-0 items-center justify-between space-x-3">
                                <span class="inline-flex size-8 items-center justify-center rounded-full bg-brand-900">
                                    <svg width="115" height="67" viewBox="0 0 115 67" fill="none"
                                        xmlns="http://www.w3.org/2000/svg" class="w-6">
                                        <path
                                            d="M114.8 33.8C114.8 15.3 102 0.299988 86.0999 0.299988C85.2999 0.299988 84.3999 0.299988 83.5999 0.399988C82.9999 0.399988 82.4999 0.499988 81.9999 0.599988C81.8999 0.599988 81.8 0.599988 81.7 0.699988C81.4 0.799988 80.7 0.899988 80.7 0.899988C80.7 0.899988 79.4999 1.19999 78.8999 1.29999C78.3999 1.39999 77.9999 1.59999 77.5999 1.79999C76.8999 1.99999 76.1999 2.29999 75.4999 2.59999C67.3999 6.19999 61.1 13.4 56.2 19C55.5 19.9 54.6999 20.7 53.9999 21.5V1.29999H47.2999V28.4C42.4999 32.9 37.5999 36 27.4999 36C22.6999 36 17.8999 34.9 13.7999 33.5V1.29999H6.99994V30.7C3.89994 29.2 1.99993 28 1.89993 28L0.199951 30.4C0.399951 30.6 2.99994 32.6 6.99994 34.8V64H13.7V37.8C17.7 39.3 22.3999 40.4 27.3999 40.4C36.5999 40.4 42.4 38.9 47.2 36.2V64H53.8999V31.1C54.6999 30.4 55.4999 29.6 56.2999 28.8C56.7999 28.3 57.2999 27.8 57.7999 27.2C58.8999 26 60 24.7 61.2 23.4C65.7 18.3 70.8999 12.2 77.2999 9.09999C77.6999 8.89999 78.2 8.69999 78.7 8.49999C79.3 8.29999 79.8999 7.99999 80.3999 7.89999C80.9999 7.69999 81.6 7.49999 82.2 7.39999C82.7 7.29999 83.2 7.19999 83.7 7.09999C83.9 7.09999 84 7.09999 84.2 6.99999C84.8 6.99999 85.3999 6.89999 85.9999 6.89999C98.0999 6.89999 107.9 18.9 107.9 33.7C107.9 41.3 105.3 48.1 101.1 53L93.8999 43.4L88.4999 47.4L95.9999 57.4C92.9999 59.3 89.4999 60.4 85.8999 60.4C73.7999 60.4 63.9999 48.5 63.8999 33.7C63.8999 33.7 63.8999 33.7 63.8999 33.6C63.8999 29.5 64.6999 25.6 66.0999 22.1C62.6999 24.8 59.6 28 57.2 31.1C57.2 31.9 57.0999 32.7 57.0999 33.6C57.0999 36 57.3 38.4 57.7 40.7C60.5 55.8 71.9999 67.1 85.7999 67.1C90.9999 67.1 95.7999 65.5 99.8999 62.7L105.5 70.1L110.9 66.1L105.1 58.3C111.1 52.4 114.8 43.6 114.8 33.8Z"
                                            fill="#F3D74A" />
                                    </svg>
                                </span>
                                <span class="sr-only">Your profile</span>
                                <span class="leading-none">
                                    <strong>
                                        {{ systemUser?.clinic?.clinicAbbr ?? systemUser?.clinic?.clinicName }}
                                    </strong><br>
                                    {{ systemUser?.firstName }} {{ systemUser?.lastName }}
                                </span>
                            </span>
                            <svg class="size-5 shrink-0 text-gray-400 group-hover:text-gray-500" viewBox="0 0 20 20"
                                fill="currentColor" data-slot="icon">
                                <path fill-rule="evenodd"
                                    d="M10.53 3.47a.75.75 0 0 0-1.06 0L6.22 6.72a.75.75 0 0 0 1.06 1.06L10 5.06l2.72 2.72a.75.75 0 1 0 1.06-1.06l-3.25-3.25Zm-4.31 9.81 3.25 3.25a.75.75 0 0 0 1.06 0l3.25-3.25a.75.75 0 1 0-1.06-1.06L10 14.94l-2.72-2.72a.75.75 0 0 0-1.06 1.06Z"
                                    clip-rule="evenodd" />
                            </svg>
                        </span>
                    </button>
                    <ul class="dropdown-menu">
                        <template v-if="!systemUser.permissions?.includes(Permissions.Settings.PreventClinicSwitch)">
                            <li v-for="c in appVm.userClinics" :key="c.id"><a role="button"
                                    @click="changeClinicContext(c.id)">{{ c.name }}</a></li>
                            <li class="divider" v-if="appVm.userClinics.length"></li>
                        </template>
                        <template v-if="systemUser.permissions?.includes(Permissions.Billing.AccessBilling)">
                            <li>
                                <router-link to="/billing">
                                    <i class="fas fa-credit-card"></i> Organization & Billing
                                </router-link>
                            </li>
                            <li class="divider"></li>
                        </template>
                        <li v-if="!isTrainingClinic">
                            <a :href="setupLookup?.identityServer + 'Account/Manage'" target="_blank"><i
                                    class="fas fa-id-card"></i> User
                                Preferences</a>
                        </li>
                        <li v-if="!isTrainingClinic">
                            <a :href="setupLookup.identityServer + 'Account/Manage/ChangePassword'" target="_blank"><i
                                    class="fas fa-wrench"></i> Change Password</a>
                        </li>
                        <li v-if="!isTrainingClinic" class="divider"></li>
                        <li><a href="/api/account/logout"><i class="fas fa-power-off"></i>&nbsp;Logout</a></li>
                    </ul>
                </div>
                <div class="p-2 grid grid-cols-1">
                    <button type="button" @click="openQuickSearch"
                        class="hidden sm:flex items-center text-left space-x-3 px-4 h-12 shadow-sm rounded-lg bg-brand-950 ring-0 cursor-pointer text-slate-300 hover:text-slate-100">
                        <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round" class="flex-none text-slate-400"
                            aria-hidden="true">
                            <path d="m19 19-3.5-3.5"></path>
                            <circle cx="11" cy="11" r="6"></circle>
                        </svg>
                        <span class="flex-auto">Quick search...</span>
                        <kbd class="font-sans font-semibold text-slate-500">
                            <abbr title="Control" class="no-underline text-slate-500">Ctrl +</abbr> K
                        </kbd>
                    </button>
                </div>
                <div class="sidebar" v-if="appVm.sidebarState.settings.menuVisible">
                    <ul>
                        <li>
                            <a role="button" @click="toggleSettingsMenu">
                                <i class="fas fa-undo"></i> Back to Main Menu
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
            <nav class="sidebar overflow-y-auto" ref="sidebarContainer">
                <ul role="list" class="space-y-1" v-if="!appVm.sidebarState.settings.menuVisible">
                    <ChqMenuItem menu-item="/dashboard" disable-pin="true">
                        <i class="fas fa-home"></i> Dashboard
                    </ChqMenuItem>
                    <ChqMenuItem v-if="systemUser?.permissions?.includes(Permissions.Calendar.View) && menuItems == 1"
                        menu-item="/calendar/spay-neuter" disable-pin="true"><i class="fas fa-calendar"></i> Spay /
                        Neuter by Capacities
                    </ChqMenuItem>
                    <ChqMenuItem v-if="systemUser?.permissions?.includes(Permissions.Calendar.View) && menuItems > 1"
                        menu-item="/calendar/spay-neuter" disable-pin="true"><i class="fas fa-calendar-alt"></i>
                        Spay / Neuter by Capacities</ChqMenuItem>
                    <ChqMenuItem v-if="systemUser?.permissions?.includes(Permissions.Calendar.View) && menuItems > 1"
                        menu-item="/calendar/wellness" disable-pin="true"><i class="fas fa-calendar-week"></i>
                        Wellness Calendar</ChqMenuItem>
                    <ChqMenuItem menu-item="/e-lobby" disable-pin="true"><i class="fas fa-columns"></i> E-Lobby
                    </ChqMenuItem>
                    <ChqMenuItem menu-item="/medical/patient-flow" disable-pin="true"><i
                            class="fas fa-file-medical"></i>
                        Patient Flow&trade;</ChqMenuItem>
                    <ChqMenuItem menu-item="/checkout/online-appointments" disable-pin="true">
                        <i class="fa-solid fa-calendar-day"></i> Online Appointments
                        <span class="badge ml-auto bg-red-600" v-show="appVm.onlineCount > 0">
                            {{ appVm.onlineCount }}
                        </span>
                    </ChqMenuItem>
                    <ChqMenuItem menu-item="/appointment/shelter-luv"
                        v-if="appVm.activeShelter == Enums.ShelterType.Shelterluv" disable-pin="true"><i
                            class="fa-solid fa-paste"></i> Shelterluv Import</ChqMenuItem>
                    <ChqMenuItem menu-item="/appointment/pet-point"
                        v-if="appVm.activeShelter == Enums.ShelterType.PetPoint" disable-pin="true"><i
                            class="fa-solid fa-paste"></i> PetPoint Import</ChqMenuItem>
                    <li v-if="uiSettings?.pinnedItems?.length">
                        <h5>Pinned</h5>
                        <ul class="sidebar-sub-menu">
                            <ChqMenuItem v-for="item in uiSettings?.pinnedItems" :menu-item="item.url" unpin="true"
                                :key="item.url">
                                {{ item.name }}
                            </ChqMenuItem>
                        </ul>
                    </li>
                    <ClientsMenu />
                    <AccountingMenu v-if="systemUser?.permissions?.includes(Permissions.Accounting.View)" />
                    <ReportsMenu v-if="systemUser?.permissions?.includes(Permissions.Reports.View)" />
                    <li v-if="systemUser?.permissions?.includes(Permissions.Settings.View)">
                        <h5>Settings</h5>
                        <a role="button" @click="toggleSettingsMenu"><i class="fas fa-cog"></i> Go to
                            Settings</a>
                    </li>
                </ul>
                <ul role="list" class="space-y-1"
                    v-if="systemUser?.permissions?.includes(Permissions.Settings.View) && appVm.sidebarState.settings.menuVisible">
                    <SettingsMenu />
                </ul>
            </nav>
            <div class="sidebar" v-if="!isTrainingClinic">
                <ul>
                    <ChqMenuItem menu-item="/help" disable-pin="true">
                        <i class="fas fa-question-circle"></i> Help & Support
                    </ChqMenuItem>
                </ul>
            </div>
            <div class="px-2 pb-1" v-if="systemUser?.isSuperAdmin && !isTrainingClinic">
                <ChqSelect v-model="clinicId" @select="onClinicChange" label="name" :options="clinics"
                    class="bg-white rounded" :reduce="c => c.id" menu-placement="top">
                    <template #option="{ option }">
                        {{ option.name }}
                    </template>
                </ChqSelect>
            </div>
        </div>
        <div v-else
            class="fixed h-full top-0 w-72 2xl:w-96 lg:group-hover/navbar:translate-x-0 grid grid-rows-[max-content_1fr_max-content] transition-transform duration-300"
            :class="{ '-translate-x-[calc(100%+16px)]': !appVm.toggleSideMenu, 'translate-x-0': appVm.toggleSideMenu, 'bg-amber-400 training': isTrainingClinic, 'bg-brand-900': !isTrainingClinic }">
            <div class="flex flex-col"></div>
            <AdminMenu />
        </div>
    </div>
    <div class="sidebar-hide" @click="toggleMenu()"
        :class="{ 'left-72 2xl:left-96': appVm.toggleSideMenu, 'left-0': !appVm.toggleSideMenu }"
        v-if="appVm.layoutMode === 'sidebar'">
        <i class="fa text-white"
            :class="{ 'fa-chevron-right': !appVm.toggleSideMenu, 'fa-chevron-left': appVm.toggleSideMenu }"></i>
    </div>
</template>
